import React from 'react';
import { Form, Input, Select, Row, Col, Button, AutoComplete, Spin } from 'antd';
import { CameraOutlined, PlusOutlined } from '@ant-design/icons';

const fieldItemStyle = { marginBottom: '15px' };

/**
 * Basic Information tab (right side of form):
 * - Basic Info: ISBN, Title, Author, Publisher, Categories — 2 fields per row
 * - Pricing/Units: Currency, Sale Price, Unit Type, Unit Measurement — 2 fields per row
 * Image uploader is rendered separately on the right in addProd.js
 */
export const BasicInfoFields = ({
  form,
  editMode,
  category,
  publisher,
  currency,
  unitType,
  onCameraClick,
  checkForUniqueness,
  onIsbnChangeImmediate,
  isbnOptions = [],
  isbnSearching = false,
  onIsbnSearch,
  onIsbnSelect,
  onIsbnInputChange,
  selectedExistingProduct = null,
  publisherSearchValue,
  setPublisherSearchValue,
  showPublisherAdd,
  setShowPublisherAdd,
  handlePublisherSearch,
  addNewPublisher,
  categorySearchValue,
  setCategorySearchValue,
  showCategoryAdd,
  setShowCategoryAdd,
  handleCategorySearch,
  addNewCategory,
}) => (
  <>
    {/* Basic Information — 2 fields per row */}
    <Row gutter={[16, 0]}>
      <Col xs={24} sm={24} md={12} lg={12} xl={12}>
        <Form.Item
          style={fieldItemStyle}
          label="ISBN"
          name="isbn"
          rules={[{ required: true, message: 'Please enter ISBN!' }]}
          extra={
            !editMode
              ? selectedExistingProduct
                ? `Existing book selected: "${selectedExistingProduct.name || selectedExistingProduct.isbn}". Update will save changes.`
                : 'Type to see matching ISBNs. Select one to autofill, or enter a new ISBN to add a book.'
              : undefined
          }
        >
          {editMode ? (
            <Input disabled style={{ backgroundColor: '#f5f5f5', color: '#999' }} />
          ) : (
            <AutoComplete
              options={isbnOptions}
              onSearch={onIsbnSearch}
              onSelect={onIsbnSelect}
              onChange={(value) => {
                onIsbnInputChange?.(value);
                onIsbnChangeImmediate?.({ target: { value } });
              }}
              onBlur={(e) => {
                const v = e?.target?.value;
                if (v) checkForUniqueness?.(v);
              }}
              placeholder="Enter ISBN to search or add new"
              filterOption={false}
              allowClear
              notFoundContent={
                isbnSearching ? (
                  <div style={{ textAlign: 'center', padding: 8 }}>
                    <Spin size="small" /> Searching…
                  </div>
                ) : (
                  <div style={{ padding: 8, color: '#666', fontSize: 12 }}>
                    No matching ISBN — fill details below to add as new
                  </div>
                )
              }
            >
              <Input
                suffix={
                  <CameraOutlined
                    onClick={onCameraClick}
                    style={{ cursor: 'pointer', color: '#1890ff', fontSize: '16px' }}
                    title="Scan ISBN with camera"
                  />
                }
              />
            </AutoComplete>
          )}
        </Form.Item>
      </Col>
      <Col xs={24} sm={24} md={12} lg={12} xl={12}>
        <Form.Item
          style={fieldItemStyle}
          label="Title"
          name="name"
          rules={[{ required: true, message: 'Please Enter Book Title!' }]}
        >
          <Input />
        </Form.Item>
      </Col>
    </Row>
    <Row gutter={[16, 0]}>
      <Col xs={24} sm={24} md={12} lg={12} xl={12}>
        <Form.Item style={fieldItemStyle} label="Author" name="author">
          <Input />
        </Form.Item>
      </Col>
      <Col xs={24} sm={24} md={12} lg={12} xl={12}>
        <Form.Item
          style={fieldItemStyle}
          name="book_publisher_id"
          label="Select Publisher"
          rules={[{ required: true, message: 'Please select publisher!' }]}
        >
          <Select
            style={{ width: '100%' }}
            loading={!publisher}
            showSearch
            placeholder="Select Publisher"
            optionFilterProp="children"
            filterOption={(input, option) => {
              const text = option?.children?.toString() || option?.label?.toString() || '';
              return text.toLowerCase().includes(input.toLowerCase());
            }}
            filterSort={(optionA, optionB) =>
              (optionA?.children || '')
                .toLowerCase()
                .localeCompare((optionB?.children || '').toLowerCase())
            }
            onSearch={handlePublisherSearch}
            onChange={() => {
              setPublisherSearchValue('');
              setShowPublisherAdd(false);
              setTimeout(() => form.validateFields(['book_publisher_id']), 100);
            }}
            notFoundContent={
              publisherSearchValue && showPublisherAdd ? (
                <div style={{ padding: '8px', textAlign: 'center' }}>
                  <div style={{ marginBottom: '8px', color: '#666' }}>
                    No publisher found for "{publisherSearchValue}"
                  </div>
                  <Button type="primary" size="small" icon={<PlusOutlined />} onClick={addNewPublisher} style={{ width: '100%' }}>
                    Add "{publisherSearchValue}"
                  </Button>
                </div>
              ) : (
                <div style={{ padding: '8px', textAlign: 'center', color: '#999' }}>No publishers found</div>
              )
            }
          >
            {publisher?.map((pub) => (
              <Select.Option key={pub.id} value={pub.id}>{pub.name}</Select.Option>
            ))}
          </Select>
        </Form.Item>
      </Col>
    </Row>
   

    {/* Currency, Sale Price, Unit Type, Unit Measurement — 2 fields per row */}
    <Row gutter={[16, 0]}>
      <Col xs={24} sm={24} md={12} lg={12} xl={12}>
        <Form.Item
          style={fieldItemStyle}
          name="product_currency_id"
          label="Select Currency"
          rules={[{ required: true, message: 'Please select currency!' }]}
          initialValue={currency?.find((cur) => cur.name?.toLowerCase().includes('rupee'))?.id}
        >
          <Select
            loading={!currency}
            showSearch
            placeholder="Select Currency"
            optionFilterProp="children"
            filterOption={(input, option) => {
              const text = option?.children?.toString() || option?.label?.toString() || '';
              return text.toLowerCase().includes(input.toLowerCase());
            }}
            filterSort={(optionA, optionB) =>
              (optionA?.children || '').toLowerCase().localeCompare((optionB?.children || '').toLowerCase())
            }
            onChange={() => setTimeout(() => form.validateFields(['product_currency_id']), 100)}
          >
            {currency?.map((cur) => (
              <Select.Option key={cur.id} value={cur.id}>{cur.name}</Select.Option>
            ))}
          </Select>
        </Form.Item>
      </Col>
      <Col xs={24} sm={24} md={12} lg={12} xl={12}>
        <Form.Item
          style={fieldItemStyle}
          label="List Price"
          name="sale_price"
          rules={[{ required: true, message: 'Please input List Price!' }]}
        >
          <Input type="number" step="0.01" />
        </Form.Item>
      </Col>
    </Row>
    <Row gutter={[16, 0]}>
      <Col xs={24} sm={24} md={12} lg={12} xl={12}>
        <Form.Item
          style={fieldItemStyle}
          label="Unit Type"
          name="unit_type"
          rules={[{ required: true, message: 'Please select unit type!' }]}
        >
          <Select>
            {unitType?.map((unit) => (
              <Select.Option key={unit} value={unit}>{unit}</Select.Option>
            ))}
          </Select>
        </Form.Item>
      </Col>
      <Col xs={24} sm={24} md={12} lg={12} xl={12}>
        <Form.Item style={fieldItemStyle} label="Unit Measurement" name="unit_measurement">
          <Input type="number" step="0.01" placeholder="Optional" />
        </Form.Item>
      </Col>
    </Row>
    <Row gutter={[16, 0]}>
      <Col xs={24} sm={24} md={12} lg={12} xl={12}>
        <Form.Item style={fieldItemStyle} name="product_category_ids" label="Select Categories">
          <Select
            mode="multiple"
            style={{ width: '100%' }}
            loading={!category}
            showSearch
            placeholder="Select Categories (Optional)"
            optionFilterProp="children"
            filterOption={(input, option) => {
              const text = option?.children?.toString() || option?.label?.toString() || '';
              return text.toLowerCase().includes(input.toLowerCase());
            }}
            filterSort={(optionA, optionB) =>
              (optionA?.children || '').toLowerCase().localeCompare((optionB?.children || '').toLowerCase())
            }
            onSearch={handleCategorySearch}
            onChange={() => {
              setCategorySearchValue('');
              setShowCategoryAdd(false);
              setTimeout(() => form.validateFields(['product_category_ids']), 100);
            }}
            notFoundContent={
              categorySearchValue && showCategoryAdd ? (
                <div style={{ padding: '8px', textAlign: 'center' }}>
                  <div style={{ marginBottom: '8px', color: '#666' }}>
                    No category found for "{categorySearchValue}"
                  </div>
                  <Button type="primary" size="small" icon={<PlusOutlined />} onClick={addNewCategory} style={{ width: '100%' }}>
                    Add "{categorySearchValue}"
                  </Button>
                </div>
              ) : (
                <div style={{ padding: '8px', textAlign: 'center', color: '#999' }}>No categories found</div>
              )
            }
          >
            {category?.map((cat) => (
              <Select.Option key={cat.id} value={cat.id}>{cat.name}</Select.Option>
            ))}
          </Select>
        </Form.Item>
      </Col>
      <Col xs={24} sm={24} md={12} lg={12} xl={12} />
    </Row>
  </>
);

/** Legacy: PricingFields kept for default ProductForm export (optional use) */
export const PricingFields = () => null;

/** Legacy: InventoryFields - no quantity/purchase price; stock tab only has StockEntriesTab */
export const InventoryFields = () => null;

/** Legacy single-form wrapper */
const ProductForm = ({
  form,
  editMode,
  category,
  publisher,
  currency,
  unitType,
  onFinish,
  onFinishFailed,
  onCameraClick,
  checkForUniqueness,
  onIsbnChangeImmediate,
  publisherSearchValue,
  setPublisherSearchValue,
  showPublisherAdd,
  setShowPublisherAdd,
  handlePublisherSearch,
  addNewPublisher,
  categorySearchValue,
  setCategorySearchValue,
  showCategoryAdd,
  setShowCategoryAdd,
  handleCategorySearch,
  addNewCategory,
  loader,
  onOpenStockModal,
  hideSubmitButton = false,
}) => (
  <Form
    form={form}
    name="basic"
    layout="vertical"
    initialValues={{
      book_publisher_id: undefined,
      product_category_ids: undefined,
      product_currency_id: undefined,
      unit_measurement: 1,
      unit_type: 'copy',
    }}
    onFinish={onFinish}
    onFinishFailed={onFinishFailed}
    autoComplete="off"
  >
    <BasicInfoFields
      form={form}
      editMode={editMode}
      category={category}
      publisher={publisher}
      currency={currency}
      unitType={unitType}
      onCameraClick={onCameraClick}
      checkForUniqueness={checkForUniqueness}
      onIsbnChangeImmediate={onIsbnChangeImmediate}
      publisherSearchValue={publisherSearchValue}
      setPublisherSearchValue={setPublisherSearchValue}
      showPublisherAdd={showPublisherAdd}
      setShowPublisherAdd={setShowPublisherAdd}
      handlePublisherSearch={handlePublisherSearch}
      addNewPublisher={addNewPublisher}
      categorySearchValue={categorySearchValue}
      setCategorySearchValue={setCategorySearchValue}
      showCategoryAdd={showCategoryAdd}
      setShowCategoryAdd={setShowCategoryAdd}
      handleCategorySearch={handleCategorySearch}
      addNewCategory={addNewCategory}
    />
    {onOpenStockModal && (
      <Row gutter={[16, 0]}>
        <Col xs={24} sm={24} md={12} lg={12} xl={12}>
          <Form.Item label=" " colon={false} style={fieldItemStyle}>
            <Button type="default" onClick={onOpenStockModal}>Stock</Button>
          </Form.Item>
        </Col>
      </Row>
    )}
    {!hideSubmitButton && (
      <Form.Item style={{ marginBottom: '15px', textAlign: 'center' }}>
        <Button type="primary" htmlType="submit" shape="round" loading={loader} style={{ minWidth: '120px' }}>
          {editMode ? 'Update' : 'Save'}
        </Button>
      </Form.Item>
    )}
  </Form>
);

export default ProductForm;
